/**
 * 设备配置 Store
 * 管理设备相关的持久化数据：设备码、加密密钥、软件 ID
 *
 * 存储格式与 v2 保持一致：
 * - localStorage key: '{prefix}csd_device_code'
 * - 值格式: { deviceCode, ekey, softId }
 *
 */

import { ref } from 'vue'
import type { IStorage } from '../storage/Storage'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { BaseStore } from './BaseStore'

export interface DeviceState {
  deviceCode: string | null
  ekey: string | null
  softId: string | null
}

export interface DeviceConfig {
  softId?: string
}

/**
 * 生成设备码（与 v2 保持一致）
 */
function generateDeviceCode(): string {
  // 使用时间戳 + 随机数生成唯一设备码（16位）
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  let result = ''
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 生成加密密钥（ekey）
 *
 * 格式规则（与 v2 保持一致）：
 * - 16位字符串
 * - 前4位：7000-9999
 * - 5-8位：['a','d','g','h','k','o','4','5','6','7','8']
 * - 9-12位：['0','1','2','3','8','9','a','b','c','d','u','i','o','p','m','n']
 * - 最后4位：1000-3999
 */
function generateEkey(): string {
  const letters1 = ['a', 'd', 'g', 'h', 'k', 'o', '4', '5', '6', '7', '8']
  const letters2 = ['0', '1', '2', '3', '8', '9', 'a', 'b', 'c', 'd', 'u', 'i', 'o', 'p', 'm', 'n']

  let s = ''
  // 前4位：7000-9999
  s += Math.floor(Math.random() * 3000) + 7000
  // 5-8位：letters1 中的字符
  s += letters1[Math.floor(Math.random() * letters1.length)]
  s += letters1[Math.floor(Math.random() * letters1.length)]
  s += letters1[Math.floor(Math.random() * letters1.length)]
  s += letters1[Math.floor(Math.random() * letters1.length)]
  // 9-12位：letters2 中的字符
  s += letters2[Math.floor(Math.random() * letters2.length)]
  s += letters2[Math.floor(Math.random() * letters2.length)]
  s += letters2[Math.floor(Math.random() * letters2.length)]
  s += letters2[Math.floor(Math.random() * letters2.length)]
  // 最后4位：1000-3999
  s += 1000 + Math.floor(Math.random() * 3000)

  return s
}

export class DeviceStore extends BaseStore<DeviceState> {
  private _deviceCode = ref<string | null>(null)
  private _ekey = ref<string | null>(null)
  private _softId = ref<string | null>(null)

  constructor(storage: IStorage) {
    super(storage, STORAGE_KEYS.DEVICE_CODE)
    this.hydrate()

    // 如果没有设备码，自动生成
    if (!this._deviceCode.value) {
      this._deviceCode.value = generateDeviceCode()
      this.persist()
    }

    // 如果没有加密密钥，自动生成
    if (!this._ekey.value) {
      this._ekey.value = generateEkey()
      this.persist()
    }
  }

  /**
   * 从存储中恢复状态（兼容 v2 和旧版 key）
   */
  hydrate(): void {
    // 先尝试从 storage 读取（会自动添加前缀）
    super.hydrate()

    // 如果已经有数据，不需要继续
    if (this._deviceCode.value && this._ekey.value) {
      return
    }

    // 兼容旧版：从 localStorage 直接读取
    if (typeof localStorage !== 'undefined') {
      const readLegacy = (key: string): string | null => {
        try {
          const value = localStorage.getItem(key)
          if (!value) return null
          try {
            const parsed = JSON.parse(value)
            // 处理 BrowserStorage 包装格式 { value, timestamp }
            if (parsed.value !== undefined) {
              if (typeof parsed.value === 'string') {
                try {
                  return JSON.parse(parsed.value)
                } catch {
                  return parsed.value
                }
              }
              return parsed.value
            }
            return parsed
          } catch {
            return value
          }
        } catch {
          return null
        }
      }

      let changed = false

      // 尝试读取旧版独立 key
      const legacyDeviceCode = readLegacy('deviceCode')
      if (!this._deviceCode.value && legacyDeviceCode) {
        this._deviceCode.value = String(legacyDeviceCode)
        changed = true
      }

      const legacyEkey = readLegacy('ekey')
      if (!this._ekey.value && legacyEkey) {
        this._ekey.value = String(legacyEkey)
        changed = true
      }

      const legacySoftId = readLegacy('soft_id')
      if (!this._softId.value && legacySoftId) {
        this._softId.value = String(legacySoftId)
        changed = true
      }

      if (changed) {
        this.persist()
      }
    }
  }

  /**
   * 持久化状态到存储（同时写入旧版 key 以保持兼容）
   */
  persist(): void {
    super.persist()

    // 同时写入旧版独立 key（兼容 v2）
    if (typeof localStorage === 'undefined') return

    const setOrRemove = (key: string, value: string | null) => {
      try {
        if (value === null || value === undefined || value === '') {
          localStorage.removeItem(key)
        } else {
          localStorage.setItem(key, value)
        }
      } catch {
        // ignore
      }
    }

    setOrRemove('deviceCode', this._deviceCode.value)
    setOrRemove('ekey', this._ekey.value)
    setOrRemove('soft_id', this._softId.value)
  }

  // Getters (只读)
  get deviceCode(): string {
    return this._deviceCode.value || ''
  }

  get ekey(): string {
    return this._ekey.value || ''
  }

  get softId(): string | null {
    return this._softId.value
  }

  /**
   * 初始化设备信息
   * 只能设置 softId，deviceCode 和 ekey 由 SDK 自动管理
   */
  initialize(config: DeviceConfig): void {
    if (config.softId) {
      this._softId.value = config.softId
      this.persist()
    }
  }

  /**
   * 获取设备参数（用于 API 请求，与 v2 CONFIG 对象格式一致）
   */
  getDeviceParams(): Record<string, string> {
    const params: Record<string, string> = {
      device_code: this.deviceCode,
      mac_code: this.deviceCode, // mac_code 等于 device_code
      ekey: this.ekey
    }
    if (this._softId.value) {
      params.soft_id = this._softId.value
    }
    return params
  }

  /**
   * 序列化状态
   */
  serialize(): string {
    return JSON.stringify({
      deviceCode: this._deviceCode.value,
      ekey: this._ekey.value,
      softId: this._softId.value
    })
  }

  /**
   * 反序列化状态
   */
  deserialize(data: string): void {
    try {
      const state = JSON.parse(data) as DeviceState
      this._deviceCode.value = state.deviceCode || null
      this._ekey.value = state.ekey || null
      this._softId.value = state.softId || null
    } catch (error) {
      console.error('[DeviceStore] Failed to deserialize state:', error)
    }
  }

  /**
   * 获取当前状态
   */
  getState(): DeviceState {
    return {
      deviceCode: this._deviceCode.value,
      ekey: this._ekey.value,
      softId: this._softId.value
    }
  }

  /**
   * 清除所有数据
   */
  clear(): void {
    this._deviceCode.value = null
    this._ekey.value = null
    this._softId.value = null
    super.clear()

    // 清除旧版 key
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem('deviceCode')
        localStorage.removeItem('ekey')
        localStorage.removeItem('soft_id')
      } catch {
        // ignore
      }
    }
  }
}
